import { getActiveSearcher } from "./activeSearcher";
import {
  QUICK_FIELDS,
  ADVANCED_FIELDS,
  buildInputSchema,
  buildSearchQueryString,
} from "./ricercaFieldRules";
import { safeRegisterTool } from "./registerTool";
import { dataResult, textResult } from "./toolResult";
import { navigate } from "gatsby";

// Pagina canonica con il RicercatoreMutuo, usata dal fallback via navigazione
// quando il tool viene invocato da una pagina priva del form.
const RICERCATORE_PATH = "/";

const summarizeSearch = (result) => {
  if (result?.isError) {
    return textResult(result.message || "Ricerca fallita", { isError: true });
  }
  const count = result?.mutui?.length || 0;
  const best = result?.mutui?.[0];
  if (count === 0) {
    return textResult("Nessun mutuo trovato per i parametri indicati.");
  }
  return dataResult(
    `Trovati ${count} mutui. Migliore: ${best.nomeMutuo}${
      best.banca ? ` di ${best.banca}` : ""
    } (rata ${best.rata}).`,
    result
  );
};

const runSearch = async (args) => {
  const active = getActiveSearcher();
  if (active) {
    return summarizeSearch(await active.runSearch(args || {}));
  }
  // Fallback: nessun ricercatore in pagina -> apri la home con la query-string.
  const { qs } = buildSearchQueryString(args || {});
  navigate(`${RICERCATORE_PATH}${qs ? `?${qs}` : ""}`);
  return textResult(
    "In questa pagina non c'e` il ricercatore: apro la pagina principale e avvio la ricerca con i parametri indicati."
  );
};

const openOnActiveOrNavigate = (args, { handlerName, route, label }) => {
  const active = getActiveSearcher();
  if (active) {
    const res = active[handlerName](args || {});
    return textResult(res.message, { isError: !!res.isError });
  }
  // Fallback senza risultati in memoria: possiamo agire solo su codiceMutuo.
  if (args?.codiceMutuo) {
    navigate(`${route}/${args.codiceMutuo}`);
    return textResult(`${label} per il mutuo ${args.codiceMutuo}.`);
  }
  return textResult(
    "Nessuna ricerca attiva in questa pagina: indica un codiceMutuo oppure esegui prima una ricerca.",
    { isError: true }
  );
};

const navIdentifierSchema = {
  type: "object",
  properties: {
    codiceMutuo: {
      type: "string",
      description: "CodiceMutuo restituito da una ricerca.",
    },
    banca: {
      type: "string",
      description: "Nome (anche parziale) della banca o del prodotto.",
    },
  },
};

/**
 * Registra (una sola volta, a livello globale) i tool WebMCP del RicercatoreMutuo.
 * Ritorna una funzione di cleanup che li deregistra tutti.
 */
export function registerRicercatoreTools(modelContext) {
  if (!modelContext) return () => {};

  const unregisters = [
    safeRegisterTool(modelContext, {
      name: "cerca_mutuo_rapido",
      description:
        "Cerca mutui sul sito con i parametri principali (finalita`, importo, valore immobile, durata, tipo tasso). I campi mancanti usano i valori di default. Funziona da qualsiasi pagina.",
      inputSchema: buildInputSchema(QUICK_FIELDS),
      execute: (args) => runSearch(args),
    }),
    safeRegisterTool(modelContext, {
      name: "cerca_mutuo_avanzato",
      description:
        "Cerca mutui sul sito con tutti i parametri disponibili, inclusi provincia, eta`, reddito e classe energetica. Funziona da qualsiasi pagina.",
      inputSchema: buildInputSchema(ADVANCED_FIELDS),
      execute: (args) => runSearch(args),
    }),
    safeRegisterTool(modelContext, {
      name: "apri_scheda_mutuo",
      description:
        "Apre la scheda di dettaglio di un mutuo dai risultati dell'ultima ricerca. Indica il CodiceMutuo oppure il nome della banca (es. 'Intesa'); almeno uno dei due e` obbligatorio.",
      inputSchema: navIdentifierSchema,
      execute: (args) =>
        openOnActiveOrNavigate(args, {
          handlerName: "openScheda",
          route: "/mutuo",
          label: "Apro la scheda",
        }),
    }),
    safeRegisterTool(modelContext, {
      name: "apri_verifica_fattibilita",
      description:
        "Avvia la verifica di fattibilita` per un mutuo dai risultati dell'ultima ricerca. Indica il CodiceMutuo oppure il nome della banca; almeno uno dei due e` obbligatorio.",
      inputSchema: navIdentifierSchema,
      execute: (args) =>
        openOnActiveOrNavigate(args, {
          handlerName: "openVerifica",
          route: "/verifica",
          label: "Avvio la verifica di fattibilita`",
        }),
    }),
  ];

  return () => unregisters.forEach((u) => typeof u === "function" && u());
}
